/**
 * Pure decision helpers for the QuickSearch docs session: which indexer to
 * prefer (local vs remote) and which status banner to surface.
 *
 * Extracted from {@link DocsQuickSearchSession} so the local-only / backend-availability
 * contract is testable without constructing the framework-scoped Entity. A
 * failed server search is named by the footer, not by a status line.
 */
import type { I18nString } from '@affine/i18n';
import type { IndexerPreferOptions } from '@affine/nbstore';

/** Snapshot of the session flags the prefer/status decision depends on. */
export interface DocsSearchModeFlags {
  isCloudWorkspace: boolean;
  isBackendAvailable: boolean;
  isServerIndexerSupported: boolean;
  isBatterySaveMode: boolean;
  isPdfSearchInCooldown: boolean;
}

/**
 * Only cloud workspaces with an available, indexer-capable server prefer the
 * remote indexer; everything else falls back to the local indexer.
 */
export function resolvePreferredDocsSearchMode(
  flags: DocsSearchModeFlags
): IndexerPreferOptions {
  return !flags.isCloudWorkspace ||
    !flags.isBackendAvailable ||
    !flags.isServerIndexerSupported
    ? 'local'
    : 'remote';
}

/**
 * User-facing status banner for the active search mode. `null` means the silent
 * happy path (remote indexer, nothing to warn about).
 */
export function statusForDocsSearchMode(
  flags: DocsSearchModeFlags,
  prefer: IndexerPreferOptions
): I18nString | null {
  if (!flags.isCloudWorkspace) {
    return { i18nKey: 'com.streamlain.quicksearch.status.local-only' };
  }
  if (
    !flags.isServerIndexerSupported ||
    (flags.isBackendAvailable && prefer === 'local')
  ) {
    return {
      i18nKey: 'com.streamlain.quicksearch.status.cloud-unavailable',
    };
  }
  if (flags.isBatterySaveMode) {
    return { i18nKey: 'com.streamlain.quicksearch.status.battery-save' };
  }
  if (flags.isPdfSearchInCooldown) {
    return {
      i18nKey: 'com.streamlain.quicksearch.status.pdf-unavailable',
    };
  }
  return null;
}

/** Shown when the local search hit its scan cap and may miss pages. */
export const DOCS_SEARCH_TRUNCATED_STATUS: I18nString = {
  i18nKey: 'com.streamlain.quicksearch.status.truncated',
};

/** Shown while the local search index cannot be read. */
export const SEARCH_INDEX_UNAVAILABLE_STATUS: I18nString = {
  i18nKey: 'com.streamlain.search.index-unavailable',
};

export const SEARCH_DEGRADED_STATUS: I18nString = {
  i18nKey: 'com.streamlain.quicksearch.status.cloud-unavailable',
};
